import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Switch, DatePicker, message, Space, Popconfirm, Tag, InputNumber, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PoweroffOutlined, UploadOutlined } from '@ant-design/icons';
import { reklamSliderService } from '../../services';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const ReklamSliders = () => {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSlider, setEditingSlider] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [form] = Form.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        fetchSliders();
    }, []);

    const fetchSliders = async () => {
        setLoading(true);
        try {
            const response = await reklamSliderService.getAllSliders();
            setSliders(response.data.data);
        } catch (error) {
            message.error(error.response?.data?.message || 'Error fetching sliders');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingSlider(null);
        form.resetFields();
        setFileList([]);
        setModalVisible(true);
    };

    const handleEdit = (slider) => {
        setEditingSlider(slider);
        form.setFieldsValue({
            ...slider,
            startDate: slider.startDate ? dayjs(slider.startDate) : null,
            endDate: slider.endDate ? dayjs(slider.endDate) : null,
        });
        // Set file list with existing image
        setFileList([{
            uid: '-1',
            name: 'Current Image',
            status: 'done',
            url: slider.imageUrl,
        }]);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await reklamSliderService.deleteSlider(id);
            message.success('Slider deleted successfully');
            fetchSliders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Error deleting slider');
        }
    };

    const handleToggle = async (id) => {
        try {
            await reklamSliderService.toggleSlider(id);
            message.success('Slider status updated');
            fetchSliders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Error toggling slider');
        }
    };

    const handleSubmit = async (values) => {
        try {
            // Validate image for new sliders
            if (!editingSlider && fileList.length === 0) {
                message.error('Please upload an image');
                return;
            }

            const formData = new FormData();
            formData.append('title', values.title);
            if (values.description) formData.append('description', values.description);
            if (values.linkUrl) formData.append('linkUrl', values.linkUrl);
            formData.append('order', values.order || 0);
            formData.append('isActive', values.isActive);
            if (values.startDate) formData.append('startDate', values.startDate.toISOString());
            if (values.endDate) formData.append('endDate', values.endDate.toISOString());

            // Add image if file selected (check both originFileObj and direct file object)
            if (fileList.length > 0) {
                const file = fileList[0].originFileObj || fileList[0];
                if (file instanceof File) {
                    formData.append('image', file);
                }
            }

            if (editingSlider) {
                await reklamSliderService.updateSlider(editingSlider._id, formData);
                message.success('Slider updated successfully');
            } else {
                await reklamSliderService.createSlider(formData);
                message.success('Slider created successfully');
            }

            setModalVisible(false);
            form.resetFields();
            setFileList([]);
            fetchSliders();
        } catch (error) {
            message.error(error.response?.data?.message || 'Error saving slider');
        }
    };

    const uploadProps = {
        beforeUpload: (file) => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                message.error('You can only upload image files!');
                return false;
            }
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('Image must be smaller than 5MB!');
                return false;
            }
            setFileList([file]);
            return false;
        },
        onRemove: () => {
            setFileList([]);
        },
        fileList,
        listType: 'picture',
        maxCount: 1,
    };

    const columns = [
        {
            title: 'Order',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            render: (url) => (
                <a href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="Slider" style={{ width: 80, height: 50, objectFit: 'cover' }} />
                </a>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Button
                        icon={<PoweroffOutlined />}
                        onClick={() => handleToggle(record._id)}
                        size="small"
                        type={record.isActive ? 'default' : 'primary'}
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this slider?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Reklam Sliders</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreate}
                >
                    Add New Slider
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={sliders}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} sliders`,
                }}
            />

            <Modal
                title={editingSlider ? 'Edit Slider' : 'Create New Slider'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setFileList([]);
                }}
                footer={null}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        isActive: true,
                        order: 0,
                    }}
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: 'Please enter title' }]}
                    >
                        <Input placeholder="Enter slider title" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea
                            placeholder="Enter slider description"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Slider Image"
                        required={!editingSlider}
                    >
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />}>
                                {editingSlider ? 'Change Image (Optional)' : 'Upload Image'}
                            </Button>
                        </Upload>
                        {editingSlider && !fileList.some(f => f.originFileObj) && (
                            <div style={{ marginTop: 8 }}>
                                <small>Current image will be kept if no new image is uploaded</small>
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="linkUrl"
                        label="Link URL (Optional)"
                    >
                        <Input placeholder="https://example.com/promotion" />
                    </Form.Item>

                    <Form.Item
                        name="order"
                        label="Display Order"
                        rules={[{ required: true, message: 'Please enter order' }]}
                    >
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="0"
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Active Status"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="startDate"
                        label="Start Date (Optional)"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="endDate"
                        label="End Date (Optional)"
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingSlider ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => {
                                setModalVisible(false);
                                form.resetFields();
                                setFileList([]);
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ReklamSliders;
